import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaClock, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO.jsx';

// EmailJS configuration – replace with your own from https://dashboard.emailjs.com
// Only the public key is exposed; service and template are safe to keep in client code.
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';

const isEmailJsConfigured = () =>
  Boolean(SERVICE_ID && TEMPLATE_ID && PUBLIC_KEY) &&
  SERVICE_ID !== 'your_service_id' &&
  TEMPLATE_ID !== 'your_template_id' &&
  PUBLIC_KEY !== 'your_public_key';

// RFC 5322–style simplified email regex (covers common valid addresses).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MAP_EMBED_SRC = 'https://maps.google.com/maps?q=108+New+Walk,+Leicester+LE1+7EA,+UK&t=&z=15&ie=UTF8&iwloc=&output=embed';

const getFieldError = (value, required, isEmail = false) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (required && !trimmed) return 'This field is required.';
  if (isEmail && trimmed && !EMAIL_REGEX.test(trimmed)) return 'Please enter a valid email address.';
  return '';
};

const ContactForm = () => {
  const formRef = useRef(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('');
  const [errors, setErrors] = useState({ from_name: '', from_email: '', subject: '', message: '' });
  const [values, setValues] = useState({ from_name: '', from_email: '', subject: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: getFieldError(value, true, name === 'from_email') }));
    if (status !== 'sending') {
      setStatus('idle');
      setStatusMessage('');
    }
  };

  const validate = () => {
    const nameError = getFieldError(values.from_name, true);
    const emailError = getFieldError(values.from_email, true, true);
    const subjectError = getFieldError(values.subject, true);
    const messageError = getFieldError(values.message, true);
    const newErrors = { from_name: nameError, from_email: emailError, subject: subjectError, message: messageError };
    setErrors(newErrors);
    return !(nameError || emailError || subjectError || messageError);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!isEmailJsConfigured()) {
      setStatus('error');
      setStatusMessage('Contact form is not configured. Please set EmailJS service, template, and public key.');
      return;
    }
    if (!validate()) {
      setStatusMessage('Please fix the errors below.');
      setStatus('error');
      return;
    }

    setStatus('sending');
    setStatusMessage('');

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, { publicKey: PUBLIC_KEY });
      setStatus('success');
      setStatusMessage('Thank you. Your message has been sent.');
      setValues({ from_name: '', from_email: '', subject: '', message: '' });
      setErrors({ from_name: '', from_email: '', subject: '', message: '' });
      formRef.current?.reset();
    } catch (err) {
      setStatus('error');
      setStatusMessage(err?.text || err?.message || 'Something went wrong. Please try again or call us.');
    }
  };

  const isSending = status === 'sending';

  const inputBase =
    'w-full px-4 py-3 rounded-xl border bg-white text-black placeholder-black/50 focus:ring-2 focus:ring-gold focus:border-gold outline-none transition duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm';
  const inputBorder = 'border-gray-300 focus:border-gold';
  const inputError = 'border-red-500 focus:ring-red-500/40 focus:border-red-500';

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6 max-w-xl mx-auto"
    >
      <div>
        <label htmlFor="contact-name" className="block text-sm font-semibold text-dark-grey mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>
          Name <span className="text-red-600" aria-hidden>*</span>
        </label>
        <input
          id="contact-name"
          type="text"
          name="from_name"
          value={values.from_name}
          onChange={handleChange}
          disabled={isSending}
          autoComplete="name"
          aria-required="true"
          aria-invalid={!!errors.from_name}
          aria-describedby={errors.from_name ? 'contact-name-error' : undefined}
          className={`${inputBase} ${errors.from_name ? inputError : inputBorder}`}
          placeholder="Your name"
        />
        {errors.from_name && (
          <p id="contact-name-error" className="mt-1.5 text-sm text-red-600" role="alert">
            {errors.from_name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-sm font-semibold text-dark-grey mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>
          Email <span className="text-red-600" aria-hidden>*</span>
        </label>
        <input
          id="contact-email"
          type="email"
          name="from_email"
          value={values.from_email}
          onChange={handleChange}
          disabled={isSending}
          autoComplete="email"
          aria-required="true"
          aria-invalid={!!errors.from_email}
          aria-describedby={errors.from_email ? 'contact-email-error' : undefined}
          className={`${inputBase} ${errors.from_email ? inputError : inputBorder}`}
          placeholder="your@email.com"
        />
        {errors.from_email && (
          <p id="contact-email-error" className="mt-1.5 text-sm text-red-600" role="alert">
            {errors.from_email}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm font-semibold text-dark-grey mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>
          Subject <span className="text-red-600" aria-hidden>*</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={values.subject}
          onChange={handleChange}
          disabled={isSending}
          autoComplete="off"
          aria-required="true"
          aria-invalid={!!errors.subject}
          aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
          className={`${inputBase} ${errors.subject ? inputError : inputBorder}`}
          placeholder="e.g. Booking enquiry"
        />
        {errors.subject && (
          <p id="contact-subject-error" className="mt-1.5 text-sm text-red-600" role="alert">
            {errors.subject}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-semibold text-dark-grey mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>
          Message <span className="text-red-600" aria-hidden>*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={values.message}
          onChange={handleChange}
          disabled={isSending}
          rows={5}
          aria-required="true"
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? 'contact-message-error' : undefined}
          className={`${inputBase} resize-y min-h-[120px] ${errors.message ? inputError : inputBorder}`}
          placeholder="Your message"
        />
        {errors.message && (
          <p id="contact-message-error" className="mt-1.5 text-sm text-red-600" role="alert">
            {errors.message}
          </p>
        )}
      </div>

      <div aria-live="polite" aria-atomic="true" className="min-h-[1.5rem]">
        {statusMessage && status === 'success' && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-gold/10 border border-gold/30 text-gold">
            <FaCheckCircle className="text-2xl shrink-0" aria-hidden />
            <p className="font-semibold">
              {statusMessage}
            </p>
          </div>
        )}
        {statusMessage && status === 'error' && (
          <p className="text-sm font-medium text-red-600">
            {statusMessage}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSending}
        className="w-full sm:w-auto bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none focus:ring-2 focus:ring-gold focus:ring-offset-2"
      >
        {isSending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
};

const Contact = () => {
  const mapContainerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Load map iframe only when the map section is in view (speeds up initial page load)
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !mapLoaded) {
          setMapLoaded(true);
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [mapLoaded]);

  // Scroll to contact-info section if hash is present in URL
  useEffect(() => {
    if (window.location.hash === '#contact-info') {
      setTimeout(() => {
        const element = document.getElementById('contact-info');
        if (element) {
          const elementTop = element.offsetTop;
          const offsetPosition = elementTop - 100; // Account for navbar
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to ensure page is loaded
    }
  }, []);

  return (
    <div className="pt-20 min-h-screen bg-white">
      <SEO
        title="Contact"
        description="Contact Bioresonance Leicester – 108 New Walk, 3rd Floor, Room 303, Leicester LE1 7EA. Opening hours Wed–Fri 9:30am–8pm, Sat–Sun 8am–8pm (Mon–Tue closed). Book your consultation."
        path="/contact"
      />
      {/* Hero / Page header */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-white via-yellow/10 to-gold/5">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10" aria-hidden />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              Contact Us
            </h1>
            <p className="text-xl text-dark-grey leading-relaxed max-w-3xl mx-auto">
              Get in touch or visit our clinic at <span className="whitespace-nowrap">108 New Walk, Leicester.</span>
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        {/* Location and Hours Section – first so map loads later */}
        <div id="contact-info" className="mt-8">
          <div className="bg-gray-50 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Location */}
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start mb-4">
                  <FaMapMarkerAlt className="text-2xl text-gold mr-3" />
                  <h3 className="text-2xl font-bold text-dark-grey" style={{ fontFamily: "Playfair Display, serif" }}>
                    Our Location
                  </h3>
                </div>
                <div className="text-gray-600 leading-relaxed">
                  <p className="font-medium mb-2">Bioresonance Leicester</p>
                  <p className="whitespace-nowrap">108 New Walk</p>
                  <p>3rd Floor, Room 303</p>
                  <p>LE1 7EA Leicester</p>
                  <div className="mt-4 flex flex-col gap-3 items-start">
                    <a 
                      href="https://www.google.com/maps/dir/?api=1&destination=108+New+Walk+Leicester+LE1+7EA+UK"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-gold hover:bg-gold/90 text-black font-semibold py-1.5 px-3 rounded-lg transition-colors duration-300 text-sm w-fit"
                    >
                      <FaMapMarkerAlt className="mr-1.5" />
                      Get Directions
                    </a>
                    <a
                      href="https://www.sumupbookings.com/t-p-wellness-ltd"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-gold hover:bg-gold/90 text-black font-semibold py-1.5 px-3 rounded-lg transition-colors duration-300 text-sm w-fit"
                    >
                      Schedule Consultation
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start mb-4">
                  <FaClock className="text-2xl text-gold mr-3" />
                  <h3 className="text-2xl font-bold text-dark-grey" style={{ fontFamily: "Playfair Display, serif" }}>
                    Opening Hours
                  </h3>
                </div>
                <div className="text-gray-600 leading-relaxed space-y-1">
                  <p><span className="font-medium text-red-600">Monday</span> <span className="text-red-600">Closed</span></p>
                  <p><span className="font-medium text-red-600">Tuesday</span> <span className="text-red-600">Closed</span></p>
                  <p><span className="font-medium">Wednesday</span> 9:30 AM – 8:00 PM</p>
                  <p><span className="font-medium">Thursday</span> 9:30 AM – 8:00 PM</p>
                  <p><span className="font-medium">Friday</span> 9:30 AM – 8:00 PM</p>
                  <p><span className="font-medium">Saturday</span> 8:00 AM – 8:00 PM</p>
                  <p><span className="font-medium">Sunday</span> 8:00 AM – 8:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section – below location & hours so it loads after user scrolls */}
        <div className="mt-16" ref={mapContainerRef}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
              Find Us
            </h2>
            <p className="text-lg text-gray-600">
              Visit our clinic at <span className="whitespace-nowrap">108 New Walk, Leicester</span>
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden min-h-[24rem] bg-gray-100">
              {mapLoaded ? (
                <iframe
                  src={MAP_EMBED_SRC}
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bioresonance Leicester Location - 108 New Walk, Leicester LE1 7EA"
                  className="w-full h-96"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center text-dark-grey">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=108+New+Walk+Leicester+LE1+7EA+UK"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:underline font-medium"
                  >
                    View 108 New Walk on Google Maps →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact form – card styling matches Prices page */}
        <div className="mt-16">
          <div className="relative overflow-hidden bg-white rounded-xl border border-gray-300 shadow-md p-8 md:p-10 max-w-4xl mx-auto">
            {/* Decorative corner accent – matches Prices cards */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-gold/10 to-transparent rounded-bl-full" aria-hidden />
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-2">
                <FaEnvelope className="text-2xl text-gold shrink-0" />
                <h2 className="text-2xl font-bold text-dark-grey text-center" style={{ fontFamily: "Playfair Display, serif" }}>
                  Send us a message
                </h2>
              </div>
              <p className="text-dark-grey text-center mb-8 max-w-xl mx-auto leading-relaxed">
                Fill in the form below and we&apos;ll get back to you as soon as we can.
              </p>
              <ContactForm />
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-gold/10 to-yellow/10 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
              Ready to Start Your Healing Journey?
            </h3>
            <p className="text-gray-600 mb-6">
              Contact our experienced founders Tomasz and Pawel today to schedule your consultation. With our comprehensive training and EU-certified medical devices, we're here to provide personalized bioresonance therapy tailored to your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:07761374278"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Call Tomasz: 0776 137 4278
              </a>
              <a 
                href="tel:07923825242"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Call Pawel: 0792 382 5242
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
